import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  FileText, 
  Download, 
  BookOpen, 
  Scale, 
  Home,
  CheckCircle,
  ArrowLeft,
  Shield,
  Users,
  Globe,
  Star,
  Bell,
  Wrench,
  CreditCard,
  ArrowRight
} from 'lucide-react';
import SubscriptionManagement from './SubscriptionManagement';

const BasicMemberPortal = ({ applicationData, onBack }) => {
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [memberInfo, setMemberInfo] = useState(null);
  const [skillsNetwork, setSkillsNetwork] = useState([]);
  const [reAgents, setReAgents] = useState([]);
  const [professionalTab, setProfessionalTab] = useState('contractors');
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [upgradeError, setUpgradeError] = useState(null);
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const [manualEmail, setManualEmail] = useState('');
  const [showSubscriptionManagement, setShowSubscriptionManagement] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Load documents and categories
        try {
          const categoriesResponse = await fetch(`${backendUrl}/api/portal/public/document-categories`);
          if (categoriesResponse.ok) {
            const categoriesData = await categoriesResponse.json();
            setCategories(categoriesData || []);
          }
        } catch (error) {
          console.error('Error loading categories:', error);
        }

        // Load skills network
        try {
          const skillsResponse = await fetch(`${backendUrl}/api/portal/public/skills-network`);
          if (skillsResponse.ok) {
            const skillsData = await skillsResponse.json();
            setSkillsNetwork(skillsData || []);
          }
        } catch (error) {
          console.error('Error loading skills network:', error);
          setSkillsNetwork([]);
        }

        // Load RE Agents
        try {
          const reAgentsResponse = await fetch(`${backendUrl}/api/re-agents/public/agents`);
          if (reAgentsResponse.ok) {
            const reAgentsData = await reAgentsResponse.json();
            setReAgents(reAgentsData.agents || []);
          }
        } catch (error) {
          console.error('Error loading RE agents:', error);
          setReAgents([]);
        }

        setLoading(false);
      } catch (error) {
        console.error('Error loading member data:', error);
        setLoading(false);
      }
    };
    
    loadData();
  }, []);


  // Document content generators
  const createPrivacyNoticeContent = () => {
    return `PRIVACY PROTECTION NOTICE
Legacy Property Council - Private Member Association

MEMBER PRIVACY RIGHTS AND DATA PROTECTION

As a member of Legacy Property Council, a Private Member Association (PMA), your privacy rights and personal information are protected under private contract law.

KEY PRIVACY PROTECTIONS:
• Your membership information is confidential and not subject to public disclosure
• Property details and tenant information remain private within the PMA
• Financial records and payment information are secured under private contract
• Communication with the Council is privileged and confidential

DATA PROTECTION MEASURES:
• All member data is encrypted and stored securely
• Access to your information is limited to authorized Council administrators
• No sharing of member information with third parties without explicit consent
• Regular security audits and compliance monitoring

YOUR RIGHTS:
• Right to access your personal data held by the Council
• Right to correct any inaccurate information
• Right to request data portability
• Right to confidential resolution of any privacy concerns

CONTACT FOR PRIVACY MATTERS:
Privacy Officer: info@lpassociation.org
Phone: (312) 899-6169

This notice is effective as of January 1, 2024.

Legacy Property Council
Private Member Association - Illinois`;
  };

  const createPropertyRightsGuide = () => {
    return `ILLINOIS PROPERTY OWNER RIGHTS GUIDE
Legacy Property Council - Member Resource

COMPREHENSIVE GUIDE TO PROPERTY OWNER RIGHTS UNDER ILLINOIS LAW

LANDLORD RIGHTS IN ILLINOIS:
• Right to collect rent as agreed in the lease
• Right to enter property with proper notice (24-48 hours typical)
• Right to evict for lease violations following proper legal process
• Right to collect security deposits (limited to 1.5x monthly rent in Chicago)
• Right to screen prospective tenants

PROPERTY MANAGEMENT RIGHTS:
• Right to set rental rates (subject to local rent control ordinances)
• Right to establish reasonable rules and regulations
• Right to require renters insurance
• Right to charge late fees if specified in lease
• Right to retain security deposit for damages beyond normal wear and tear

LEGAL PROTECTIONS:
• Protection against tenant retaliation for exercising legal rights
• Right to legal remedies for property damage
• Right to fair housing compliance protections
• Protection under private contract law as PMA member

EVICTION PROCESS RIGHTS:
• Right to serve proper notices (5-day pay or quit, 10-day cure or quit)
• Right to file for possession in court
• Right to legal representation throughout process
• Right to recover unpaid rent and damages

MAINTENANCE AND REPAIRS:
• Right to require tenant reporting of maintenance issues
• Right to enter for emergency repairs without notice
• Right to deduct repair costs from security deposit
• Right to establish maintenance standards

KEY ILLINOIS STATUTES:
• Illinois Residential Tenant Landlord Act (765 ILCS 705/0.01 et seq.)
• Illinois Human Rights Act
• Chicago Residential Landlord Tenant Ordinance (if applicable)

For legal advice specific to your situation, consult with a qualified attorney.

Legacy Property Council - Protecting Property Owner Rights`;
  };

  const createLeaseAgreementTemplate = () => {
    return `STANDARD LEASE AGREEMENT TEMPLATE
State of Illinois - Legacy Property Council

RESIDENTIAL LEASE AGREEMENT

Property Address: _________________________________
City: _________________ State: IL Zip: ____________

PARTIES:
Landlord: _______________________________________
Tenant(s): _____________________________________

LEASE TERMS:
Lease Term: From _______ to _______
Monthly Rent: $_________ Due on the _____ of each month
Security Deposit: $_________ 
Pet Deposit (if applicable): $_________ 

RENT PAYMENT:
• Rent is due on or before the ___ day of each month
• Late fee of $____ applies after ___ days past due date
• Payment methods accepted: _______________________
• Rent payments should be made to: ________________

PROPERTY USE:
• Property is to be used solely as a private residence
• Maximum occupancy: _____ persons
• No subletting without written landlord consent
• No illegal activities permitted on premises

TENANT RESPONSIBILITIES:
• Maintain cleanliness and sanitation of premises
• Pay all utilities as agreed: ___________________
• Report maintenance issues promptly to landlord
• Comply with all applicable laws and ordinances
• No alterations without written landlord permission

LANDLORD RESPONSIBILITIES:
• Provide habitable living conditions
• Make necessary repairs as required by law
• Respect tenant's right to peaceful enjoyment
• Provide 24-hour notice before entering (except emergencies)

SECURITY DEPOSIT:
• Deposit will be returned within 45 days of lease termination
• Deductions may be made for unpaid rent, cleaning, or damages
• Interest on deposit will be paid as required by local ordinance

TERMINATION:
• __ days written notice required for lease termination
• Property must be returned in original condition (normal wear excepted)
• All keys and remotes must be returned to landlord

This template is provided by Legacy Property Council for member use.
Consult with legal counsel for specific situations.

Landlord Signature: _________________ Date: _______
Tenant Signature: __________________ Date: _______`;
  };

  const createTenantApplicationForm = () => {
    return `TENANT APPLICATION FORM
Legacy Property Council - Property Management Resource

RENTAL APPLICATION

Property Address: ___________________________________
Monthly Rent: $_______ Security Deposit: $__________
Available Date: _______________

APPLICANT INFORMATION:
Full Legal Name: ____________________________________
Current Address: ____________________________________
City: _______________ State: _____ Zip: ___________
Phone: _____________ Email: ______________________
Date of Birth: ____________ SSN: __________________
Driver's License #: _________________________________

EMPLOYMENT INFORMATION:
Current Employer: ___________________________________
Job Title: _______________________________________
Supervisor Name: ___________________________________
Work Address: ____________________________________
Work Phone: _____________ Years Employed: __________
Monthly Gross Income: $____________________________

ADDITIONAL INCOME SOURCES:
Source: _________________________________________ 
Monthly Amount: $________________________________

RENTAL HISTORY (Last 3 residences):
Current/Most Recent:
Address: _______________________________________ 
Landlord Name: _________________________________
Phone: _______________ Rent Amount: $____________
Move-in Date: _______ Move-out Date: _____________
Reason for Leaving: ____________________________

Previous Residence:
Address: _______________________________________ 
Landlord Name: _________________________________
Phone: _______________ Rent Amount: $____________
Move-in Date: _______ Move-out Date: _____________

REFERENCES:
Personal Reference 1:
Name: _________________ Relationship: _____________
Phone: _______________ Email: ____________________

Personal Reference 2:
Name: _________________ Relationship: _____________
Phone: _______________ Email: ____________________

ADDITIONAL INFORMATION:
Number of Occupants: ____________________________
Pet Information: ________________________________
Vehicle Information: ___________________________
Emergency Contact: _____________________________

AUTHORIZATION:
I authorize Legacy Property Council and the property owner to:
• Verify employment and income information
• Contact previous landlords and references
• Obtain credit report and background check
• Verify any other information provided

Applicant Signature: _________________ Date: _______

FOR OFFICE USE ONLY:
Application Fee Received: $_______ Date: ___________
Credit Score: _______ Background Check: ____________
Approved: _____ Denied: _____ Date: _______________
Comments: _____________________________________`;
  };

  const createPropertyInspectionChecklist = () => {
    return `PROPERTY INSPECTION CHECKLIST
Move-In/Move-Out Inspection - Legacy Property Council

Property Address: ___________________________________
Inspection Type: ☐ Move-In ☐ Move-Out ☐ Periodic
Date: _____________ Inspector: ____________________

CONDITION RATINGS: E=Excellent, G=Good, F=Fair, P=Poor, D=Damaged

LIVING ROOM:
☐ Walls/Paint           Rating: ___ Notes: ___________
☐ Flooring             Rating: ___ Notes: ___________
☐ Windows/Screens      Rating: ___ Notes: ___________
☐ Light Fixtures       Rating: ___ Notes: ___________
☐ Electrical Outlets   Rating: ___ Notes: ___________
☐ Doors               Rating: ___ Notes: ___________

KITCHEN:
☐ Cabinets            Rating: ___ Notes: ___________
☐ Countertops         Rating: ___ Notes: ___________
☐ Appliances          Rating: ___ Notes: ___________
☐ Sink/Faucet         Rating: ___ Notes: ___________
☐ Flooring            Rating: ___ Notes: ___________
☐ Windows             Rating: ___ Notes: ___________

BATHROOM(S):
☐ Toilet              Rating: ___ Notes: ___________
☐ Shower/Tub          Rating: ___ Notes: ___________
☐ Sink/Vanity         Rating: ___ Notes: ___________
☐ Mirror              Rating: ___ Notes: ___________
☐ Exhaust Fan         Rating: ___ Notes: ___________
☐ Flooring            Rating: ___ Notes: ___________

BEDROOMS:
Bedroom 1:
☐ Walls/Paint         Rating: ___ Notes: ___________
☐ Flooring           Rating: ___ Notes: ___________
☐ Windows/Screens    Rating: ___ Notes: ___________
☐ Closet             Rating: ___ Notes: ___________

Bedroom 2:
☐ Walls/Paint         Rating: ___ Notes: ___________
☐ Flooring           Rating: ___ Notes: ___________
☐ Windows/Screens    Rating: ___ Notes: ___________
☐ Closet             Rating: ___ Notes: ___________

GENERAL CONDITIONS:
☐ HVAC System         Rating: ___ Notes: ___________
☐ Water Heater        Rating: ___ Notes: ___________
☐ Smoke Detectors     Rating: ___ Notes: ___________
☐ Carbon Monoxide Det. Rating: ___ Notes: ___________
☐ Exterior/Yard       Rating: ___ Notes: ___________

KEYS PROVIDED:
☐ Front Door Key      Quantity: ___
☐ Back Door Key       Quantity: ___
☐ Mailbox Key         Quantity: ___
☐ Other Keys          Quantity: ___ Description: ____

ADDITIONAL NOTES:
_________________________________________________
_________________________________________________
_________________________________________________

SIGNATURES:
Tenant: ________________________ Date: ___________
Landlord/Agent: _________________ Date: ___________

This inspection form is provided by Legacy Property Council
for member property management use.`;
  };

  const createPropertyManagementGuide = () => {
    return `PROPERTY MANAGEMENT BASICS FOR NEW OWNERS
Legacy Property Council - Educational Resource

ESSENTIAL GUIDE FOR FIRST-TIME PROPERTY OWNERS AND MANAGERS

GETTING STARTED:
Property management involves overseeing residential rental properties to maximize 
return on investment while maintaining legal compliance and tenant satisfaction.

KEY RESPONSIBILITIES:
• Tenant screening and selection
• Rent collection and financial management  
• Property maintenance and repairs
• Legal compliance and documentation
• Emergency response and communication

TENANT SCREENING BEST PRACTICES:
1. Verify Employment and Income
   - Require pay stubs and employment verification
   - Income should be 2.5-3x monthly rent
   - Contact current employer directly

2. Check Rental History
   - Contact previous landlords (not just current)
   - Ask about payment history, property care, lease compliance
   - Verify reasons for moving

3. Credit and Background Checks
   - Run comprehensive credit reports
   - Check for criminal history (follow Fair Housing laws)
   - Look for consistent payment patterns

RENT COLLECTION STRATEGIES:
• Set clear payment policies and deadlines
• Offer multiple payment methods (online, check, money order)
• Implement late fees fairly and consistently
• Document all payment communications
• Consider automatic payment incentives

MAINTENANCE MANAGEMENT:
Preventive Maintenance:
- Schedule regular HVAC servicing
- Annual plumbing and electrical inspections
- Seasonal property walkthroughs
- Keep detailed maintenance records

Emergency Procedures:
- Establish 24/7 emergency contact system
- Maintain list of trusted contractors
- Define what constitutes an emergency
- Have emergency fund readily available

LEGAL COMPLIANCE:
Federal Laws:
- Fair Housing Act compliance
- Lead-based paint disclosure (pre-1978 properties)
- Americans with Disabilities Act considerations

Illinois State Laws:
- Illinois Residential Landlord Tenant Act
- Security deposit regulations
- Proper notice requirements for entry/eviction

Local Ordinances:
- Check city-specific rental regulations
- Rental registration requirements
- Local rent control laws (Chicago, etc.)

FINANCIAL MANAGEMENT:
• Separate business and personal accounts
• Track all income and expenses
• Save for major repairs and vacancies
• Understand tax implications and deductions
• Consider professional accounting services

INSURANCE CONSIDERATIONS:
- Landlord/rental property insurance
- Liability coverage
- Loss of rent coverage  
- Require tenant renters insurance
- Consider umbrella policies for multiple properties

RECORD KEEPING:
Essential Documents:
- Signed lease agreements
- Tenant applications and screening results
- Inspection reports and photos
- Maintenance and repair records
- Financial records and receipts

COMMON MISTAKES TO AVOID:
• Inadequate tenant screening
• Informal lease agreements
• Poor maintenance records
• Inconsistent rule enforcement
• Mixing personal and business finances
• Ignoring local rental laws

SUCCESS TIPS:
1. Treat property management as a business
2. Build relationships with reliable contractors
3. Stay current on rental laws and regulations
4. Maintain professional boundaries with tenants
5. Consider property management software for organization
6. Network with other property owners and professionals

WHEN TO CONSIDER PROFESSIONAL MANAGEMENT:
- Multiple properties requiring significant time
- Long-distance property ownership
- Lack of time for hands-on management
- Desire to reduce personal involvement
- Complex legal or maintenance issues

For additional resources and support, contact:
Legacy Property Association Member Services
info@lpassociation.org
(312) 899-6169

This guide is provided for educational purposes. Consult with
legal and financial professionals for specific situations.`;
  };

  const createTenantRightsGuide = () => {
    return `UNDERSTANDING ILLINOIS TENANT RIGHTS
Legacy Property Council - Educational Resource

GUIDE TO ILLINOIS TENANT RIGHTS AND LANDLORD OBLIGATIONS

TENANT RIGHTS UNDER ILLINOIS LAW:

RIGHT TO HABITABLE CONDITIONS:
• Property must meet basic health and safety standards
• Working plumbing, heating, and electrical systems required
• Adequate hot water and reasonable temperatures
• Proper ventilation and natural light
• Freedom from pest infestations

RIGHT TO PRIVACY:
• Landlord must give 24-hour notice before entering (except emergencies)
• Entry must be at reasonable times and for legitimate purposes
• No harassment or excessive entry attempts
• Right to peaceful enjoyment of the property

SECURITY DEPOSIT RIGHTS:
• Maximum deposit limited by local ordinances (Chicago: 1.5x monthly rent)
• Interest must be paid on deposits (where required by local law)
• Itemized list of deductions required within 30-45 days
• Right to contest improper deductions

REPAIR AND MAINTENANCE RIGHTS:
• Right to request necessary repairs
• Protection from retaliation for repair requests
• Right to withhold rent in extreme cases (with proper legal procedures)
• Right to make emergency repairs and deduct costs (limited circumstances)

ANTI-DISCRIMINATION PROTECTIONS:
Protected Classes Under Fair Housing:
- Race, color, national origin
- Religion, sex, familial status
- Disability/handicap
- Additional local protections may apply

EVICTION PROTECTIONS:
• Right to proper notice before eviction proceedings
• Right to cure lease violations (when applicable)  
• Right to legal representation in eviction proceedings
• Protection from self-help evictions (landlord cannot lock out tenant)

RENT AND PAYMENT RIGHTS:
• Right to receipt for rent payments
• Protection from arbitrary rent increases during lease term
• Right to reasonable payment methods
• Protection from excessive late fees

LANDLORD OBLIGATIONS:

MAINTENANCE OBLIGATIONS:
• Keep property in habitable condition
• Make necessary repairs promptly
• Maintain common areas safely
• Ensure compliance with building codes
• Provide adequate trash collection

DISCLOSURE OBLIGATIONS:
• Lead-based paint disclosure (pre-1978 properties)
• Known defects or hazards
• Contact information for emergencies
• Proper identification as landlord or agent

NOTICE REQUIREMENTS:
• 24-hour notice for non-emergency entry
• Proper notice for lease termination
• Notice of rent increases (typically 30 days)
• Notice of changes to lease terms

CHICAGO-SPECIFIC RIGHTS:
(If property is in Chicago)
• Residential Landlord Tenant Ordinance (RLTO) protections
• Interest on security deposits required
• Just cause eviction protections
• Specific notice and procedure requirements

TENANT REMEDIES:
If landlord violates tenant rights:
• File complaint with local housing authority
• Seek legal advice from tenant rights organizations
• Document all communications and issues
• Consider legal action for damages
• Report code violations to city inspectors

RESOURCES FOR TENANTS:
• Illinois Residential Landlord Tenant Act (765 ILCS 705/)
• Chicago Department of Housing (if applicable)
• Legal aid organizations
• Tenant rights hotlines
• Local housing authorities

IMPORTANT NOTES FOR LANDLORDS:
Understanding tenant rights helps property owners:
• Maintain legal compliance
• Avoid costly legal disputes
• Build positive tenant relationships
• Protect investment through proper procedures
• Ensure fair and professional management practices

DISPUTE RESOLUTION:
• Attempt direct communication first
• Document all interactions
• Consider mediation services
• Seek legal counsel when necessary
• Know local court procedures

This guide is for educational purposes only.
For specific legal advice, consult with a qualified attorney
familiar with Illinois landlord-tenant law.

Legacy Property Council - Private Member Association
Supporting Property Owners Through Education and Resources`;
  };

  const createTaxGuide = () => {
    return `TAX IMPLICATIONS FOR PROPERTY OWNERS
Legacy Property Council - Educational Resource

UNDERSTANDING TAX BENEFITS AND OBLIGATIONS FOR RENTAL PROPERTY OWNERS

RENTAL INCOME TAXATION:
• All rental income must be reported to the IRS
• Income includes rent, security deposits kept, and other fees
• Report income in the year received (cash basis)
• Use Schedule E (Supplemental Income and Loss) for reporting

DEDUCTIBLE EXPENSES:
Operating Expenses (100% deductible):
- Property management fees
- Repairs and maintenance
- Insurance premiums
- Property taxes
- Utilities (if landlord-paid)
- Legal and professional fees
- Travel expenses for property management
- Advertising for tenants

DEPRECIATION BENEFITS:
• Residential rental property: 27.5-year straight-line depreciation
• Only the building (not land) can be depreciated
• Major improvements must be depreciated over time
• Keep detailed records of property basis and improvements

CAPITAL IMPROVEMENTS vs. REPAIRS:
Repairs (Immediately Deductible):
- Fixing leaky faucets
- Painting touch-ups
- Replacing broken windows
- Minor appliance repairs

Capital Improvements (Depreciated):
- New roof installation
- HVAC system replacement
- Kitchen renovations
- Adding new rooms or structures

RECORD KEEPING REQUIREMENTS:
Essential Tax Records:
- All rental income received
- Receipts for deductible expenses
- Bank statements and canceled checks
- Lease agreements and tenant correspondence
- Property purchase documents
- Improvement and repair documentation

IMPORTANT TAX CONSIDERATIONS:

1. Self-Employment Tax:
   - Rental income generally not subject to SE tax
   - Exception: Providing substantial services to tenants

2. Passive Activity Loss Rules:
   - Rental activities typically considered passive
   - Losses may be limited if income exceeds thresholds
   - Active participation may allow up to $25,000 annual loss deduction

3. Section 1031 Exchanges:
   - Like-kind property exchanges can defer capital gains
   - Strict timing and procedural requirements
   - Professional guidance strongly recommended

STATE AND LOCAL TAX CONSIDERATIONS:
Illinois Specific:
• Illinois state income tax applies to rental income
• Property taxes vary by county and municipality
• Some local rental taxes or licensing fees may apply
• Chicago has specific rental registration requirements

QUARTERLY ESTIMATED TAXES:
• Required if expecting to owe $1,000+ in taxes
• Based on previous year's tax liability or current year estimate
• Due January 15, April 15, June 15, and September 15
• Avoid penalties by paying 25% of annual liability each quarter

BUSINESS ENTITY CONSIDERATIONS:
Sole Proprietorship:
- Simplest structure, report on personal return
- Personal liability for property-related issues
- All income subject to personal tax rates

LLC Benefits:
- Limited liability protection
- Potential tax flexibility
- Professional appearance for multiple properties
- May complicate financing initially

TAX PLANNING STRATEGIES:
• Time major repairs and improvements strategically
• Consider energy-efficient improvements (potential credits)
• Plan property sales for optimal tax timing
• Maximize deductible expenses through proper documentation
• Consider professional tax preparation for complex situations

COMMON TAX MISTAKES:
• Mixing personal and business expenses
• Inadequate record keeping
• Misclassifying repairs vs. improvements
• Forgetting to report all rental income
• Claiming personal use portions as deductible

YEAR-END TAX CHECKLIST:
□ Compile all rental income records
□ Organize deductible expense receipts
□ Calculate depreciation for the year
□ Review estimated tax payments made
□ Plan any year-end purchases or repairs
□ Consult with tax professional if needed

RETIREMENT AND ESTATE PLANNING:
• Rental properties in retirement income planning
• Estate tax implications for inherited property
• Consider gifting strategies for multiple properties
• Plan for stepped-up basis benefits

PROFESSIONAL GUIDANCE:
When to Consult Tax Professionals:
- Multiple rental properties
- Complex business structures
- Significant capital improvements
- 1031 exchange transactions  
- IRS audit or dispute situations

IMPORTANT DISCLAIMERS:
This information is for educational purposes only and does not
constitute tax advice. Tax laws change frequently and individual
situations vary significantly.

Always consult with qualified tax professionals for:
• Specific tax advice
• Current law updates
• Individual situation analysis
• Professional tax preparation
• IRS representation if needed

Legacy Property Association provides this educational resource to
support member success in property investment and management.

For additional resources: support@legacypropertyassociation.com`;
  };

  const createScheduleEGuide = () => {
    return `IRS SCHEDULE E GUIDE FOR RENTAL PROPERTIES
Legacy Property Council - Educational Resource

COMPREHENSIVE GUIDE TO SCHEDULE E REPORTING, EXPENSE TRACKING & DEPRECIATION

WHAT IS SCHEDULE E?
Schedule E (Supplemental Income and Loss) is the IRS form used to report income 
and expenses from rental real estate properties. All rental property owners must 
file Schedule E annually with their tax return.

SCHEDULE E STRUCTURE:
Part I: Income or Loss From Rental Real Estate and Royalties
- Property address and type
- Rental income received
- Deductible expenses
- Depreciation calculations
- Net rental income or loss

RENTAL INCOME REPORTING (Lines 3-4):
What to Include:
• Monthly rent payments received
• Security deposits forfeited by tenant
• Advance rent payments
• Fees for services (pet fees, late fees if not security deposits)
• Insurance proceeds for lost rental income

What NOT to Include:
• Security deposits you intend to return
• Loans from tenants
• Your own contributions for improvements

DEDUCTIBLE RENTAL EXPENSES (Lines 5-18):

Line 5 - Advertising:
- Newspaper ads for tenants
- Online listing fees (Zillow, Apartments.com)
- For Rent signs and materials
- Marketing materials and photography

Line 6 - Auto and Travel:
- Mileage to/from rental property (standard mileage rate)
- Gas, maintenance for property-related travel
- Parking fees for property visits
- Travel to hardware stores for repairs

Line 7 - Cleaning and Maintenance:
- Regular cleaning between tenants
- Lawn care and landscaping
- Minor repairs and maintenance
- Pest control services
- Pool maintenance

Line 8 - Commissions:
- Real estate agent commissions for finding tenants
- Property management company fees
- Leasing agent commissions

Line 9 - Insurance:
- Landlord/rental property insurance premiums
- Liability insurance
- Flood insurance (if applicable)
- Workers' compensation (if you have employees)

Line 10 - Legal and Professional Services:
- Attorney fees for evictions or legal issues
- Accountant fees for tax preparation
- Property management services
- Real estate professional consultations

Line 11 - Management Fees:
- Property management company fees
- Fees paid to rental management services
- Administrative services for property oversight

Line 12 - Mortgage Interest:
- Interest paid on rental property mortgages
- Points paid on rental property loans
- Home equity loan interest (if used for rental property)

Line 13 - Other Interest:
- Credit card interest for rental-related purchases
- Personal loan interest for property improvements
- Equipment financing interest

Line 14 - Repairs:
IMPORTANT DISTINCTION - Repairs vs. Improvements:

Repairs (Immediately Deductible):
- Fixing leaky pipes
- Replacing broken windows
- Painting to maintain current condition
- Fixing appliances
- Replacing worn carpeting with similar quality

Improvements (Must be Depreciated):
- Adding rooms or structures
- Installing new heating/cooling systems
- Complete kitchen renovations
- New roofing (unless emergency repair)
- Installing hardwood floors where carpet existed

Line 15 - Supplies:
- Light bulbs and batteries
- Cleaning supplies
- Small tools under $2,500
- Hardware and materials for minor repairs
- Office supplies for property management

Line 16 - Taxes:
- Real estate taxes
- Personal property taxes on appliances/furniture
- State and local taxes related to rental activity

Line 17 - Utilities:
- Electricity, gas, water, sewer (if landlord-paid)
- Trash collection
- Internet/cable (if included in rent)

Line 18 - Depreciation:
RENTAL PROPERTY DEPRECIATION RULES:

Residential Rental Property: 27.5-year straight-line depreciation
Commercial Property: 39-year straight-line depreciation

Calculating Depreciation:
1. Determine property basis (purchase price + closing costs + improvements)
2. Subtract land value (land cannot be depreciated)
3. Divide building value by 27.5 years
4. Claim annual depreciation amount

Example:
- Purchase price: $200,000
- Closing costs: $5,000
- Land value: $40,000
- Depreciable basis: $165,000 ($200,000 + $5,000 - $40,000)
- Annual depreciation: $6,000 ($165,000 ÷ 27.5)

DEPRECIATION RECORD KEEPING:
Essential Records:
• Original purchase documents
• Closing statements
• Improvement receipts and invoices
• Property appraisals showing land vs. building value
• Form 4562 (Depreciation and Amortization) for first year

PASSIVE ACTIVITY LOSS RULES:
• Rental activity generally considered "passive"
• Losses may be limited if your adjusted gross income exceeds $100,000
• Up to $25,000 in rental losses can offset other income if you actively participate
• Active participation requires at least 10% ownership and meaningful decisions

YEAR-END SCHEDULE E CHECKLIST:
□ Compile all rental income records (rent rolls, bank deposits)
□ Organize receipts by expense category (Lines 5-18)
□ Calculate total miles driven for rental property visits
□ Determine depreciation for the tax year
□ Separate repairs from capital improvements
□ Review mortgage statements for interest amounts
□ Gather insurance premium records
□ Collect property tax statements
□ Document any casualty losses or theft

COMMON SCHEDULE E MISTAKES:
• Claiming improvements as repairs
• Including personal expenses as rental expenses
• Forgetting to report all rental income
• Incorrect depreciation calculations
• Mixing personal and rental property expenses
• Not maintaining adequate records

SCHEDULE E SOFTWARE AND TOOLS:
Recommended Tax Software:
- TurboTax Premier or Business
- H&R Block Premium & Business
- FreeTaxUSA Deluxe
- Professional tax preparation software

Record Keeping Apps:
- QuickBooks for rental properties
- Stessa (free rental property management)
- Landlord Studio
- RentRedi
- Buildium

PREMIUM MEMBER ADVANTAGE:
📊 Legacy Property Council PREMIUM members receive automated 
Reporting Tools that organize all your rental data including:

✓ Comprehensive income summaries by property and month
✓ Expenses automatically categorized by Schedule E line items
✓ Maintenance and repair costs properly classified
✓ Year-end summary reports ready for tax filing
✓ Monthly and quarterly reports for estimated tax planning
✓ Professional formatting suitable for CPA handoff
✓ Historical data tracking for multi-year tax planning

ESTIMATED TAX PAYMENTS:
If you expect to owe $1,000+ in taxes:
- Q1: January 15
- Q2: April 15  
- Q3: June 15
- Q4: September 15

Calculate based on:
• Previous year's tax liability, OR
• 90% of current year's expected tax

STATE TAX CONSIDERATIONS:
Illinois Specifics:
• Illinois taxes rental income at state rates
• Property taxes vary by county/municipality
• Some cities have rental licensing requirements
• Chicago has specific landlord registration rules

ADVANCED TAX STRATEGIES:
• Section 1031 like-kind exchanges
• Cost segregation studies for commercial properties
• Bonus depreciation for certain improvements
• Conservation easements (consult professionals)
• Real Estate Professional status election

AUDIT PREPARATION:
Keep These Records for 7 Years:
- All rental agreements and leases
- Bank statements showing rental income
- Receipts for all deductible expenses
- Mileage logs with dates and purposes
- Before/after photos of repairs
- Contractor invoices and permits
- Insurance claims and settlements

WHEN TO HIRE PROFESSIONALS:
Consider Professional Help For:
• Multiple rental properties
• Complex ownership structures (LLCs, partnerships)
• Significant capital improvements
• 1031 exchanges
• Real Estate Professional election
• Previous IRS notices or audits

RED FLAGS TO AVOID:
• Excessive or unusual deductions
• Round numbers throughout the return
• Claiming 100% business use of vehicles
• Large losses year after year
• Inconsistent reporting between years

FUTURE TAX PLANNING:
Year-Round Strategies:
- Track expenses monthly, not yearly
- Plan major improvements for tax efficiency
- Consider timing of property sales
- Maximize depreciation benefits
- Plan for depreciation recapture on sales

This guide provides foundational knowledge for Schedule E reporting.
For personalized advice and professional assistance with your specific
tax situation, consider upgrading to Premium membership.

Legacy Property Association - Supporting Property Owner Success
For tax professional referrals: info@lpassociation.org

DISCLAIMER: This educational material does not constitute tax advice. 
Always consult with qualified tax professionals for your specific situation.`;
  };

  const handleDocumentDownload = (documentId, title, event) => {
    // Create a more realistic download experience
    const button = event.currentTarget;
    const originalText = button.textContent;
    
    // Show download in progress
    button.textContent = 'Downloading...';
    button.disabled = true;
    
    // Simulate download process
    setTimeout(() => {
      // Create a mock file download
      const element = document.createElement('a');
      
      // Create mock file content based on document type
      let fileContent = '';
      let fileName = '';
      let mimeType = 'text/plain';
      
      if (title.includes('Privacy Protection Notice')) {
        fileContent = createPrivacyNoticeContent();
        fileName = 'Privacy_Protection_Notice.txt';
      } else if (title.includes('Illinois Property Owner Rights')) {
        fileContent = createPropertyRightsGuide();
        fileName = 'Illinois_Property_Owner_Rights_Guide.txt';
      } else if (title.includes('Lease Agreement')) {
        fileContent = createLeaseAgreementTemplate();
        fileName = 'Standard_Lease_Agreement_Illinois.txt';
      } else if (title.includes('Tenant Application')) {
        fileContent = createTenantApplicationForm();
        fileName = 'Tenant_Application_Form.txt';
      } else if (title.includes('Property Inspection')) {
        fileContent = createPropertyInspectionChecklist();
        fileName = 'Property_Inspection_Checklist.txt';
      } else if (title.includes('Property Management Basics')) {
        fileContent = createPropertyManagementGuide();
        fileName = 'Property_Management_Basics.txt';
      } else if (title.includes('Understanding Illinois Tenant Rights')) {
        fileContent = createTenantRightsGuide();
        fileName = 'Illinois_Tenant_Rights_Guide.txt';
      } else if (title.includes('Tax Implications')) {
        fileContent = createTaxGuide();
        fileName = 'Tax_Implications_Property_Owners.txt';
      } else if (title.includes('IRS Schedule E Guide')) {
        fileContent = createScheduleEGuide();
        fileName = 'IRS_Schedule_E_Guide_Rental_Properties.txt';
      } else {
        fileContent = `${title}\n\nThis is a sample document from Legacy Property Association.\n\nFor the full version of this document, please contact info@lpassociation.org\n\nLegacy Property Association - Private Member Association`;
        fileName = `${title.replace(/[^a-z0-9]/gi, '_')}.txt`;
      }
      
      // Create downloadable file
      const file = new Blob([fileContent], { type: mimeType });
      element.href = URL.createObjectURL(file);
      element.download = fileName;
      
      // Trigger download
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
      
      // Reset button
      button.textContent = originalText;
      button.disabled = false;
      
      // Show success message
      alert(`✅ Downloaded: ${title}\n\nThe document has been saved to your Downloads folder.`);
      
    }, 1500); // 1.5 second delay to simulate download
  };

  const getDocumentsByCategory = (categoryName) => {
    return documents.filter(doc => doc.category === categoryName);
  };

  const getCategoryIcon = (categoryName) => {
    switch (categoryName) {
      case 'Legal Documents':
        return Scale;
      case 'Property Forms':
        return FileText;
      case 'Educational Resources':
        return BookOpen;
      default:
        return FileText;
    }
  };

  const handleUpgrade = async (targetLevel) => {
    setUpgrading(true);
    setUpgradeError(null);

    try {
      // Get member email from multiple possible sources
      let memberEmail = applicationData?.email;
      
      // Try to get email from original application data
      if (!memberEmail) {
        const originalApplication = JSON.parse(localStorage.getItem('legacyPropertyApplication') || '{}');
        memberEmail = originalApplication.email;
      }
      
      // Try to get email from basic member application
      if (!memberEmail) {
        const basicMember = JSON.parse(localStorage.getItem('basicMemberApplication') || '{}');
        memberEmail = basicMember.email;
      }
      
      if (!memberEmail) {
        // Show email prompt instead of throwing error
        setShowEmailPrompt(true);
        setUpgrading(false);
        return;
      }

      const upgradeRequest = {
        current_membership_level: 'Basic',
        target_membership_level: targetLevel,
        billing_frequency: 'monthly',
        member_email: memberEmail
      };

      const response = await fetch(`${backendUrl}/api/upgrades/initiate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(upgradeRequest),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Redirect to Stripe checkout
        window.location.href = data.checkout_url;
      } else {
        throw new Error(data.detail || 'Failed to initiate upgrade');
      }
    } catch (error) {
      console.error('Error initiating upgrade:', error);
      setUpgradeError(error.message);
    } finally {
      setUpgrading(false);
    }
  };

  const handleEmailSubmit = () => {
    if (manualEmail && manualEmail.includes('@')) {
      // Store the email in localStorage for future use
      const basicMember = JSON.parse(localStorage.getItem('basicMemberApplication') || '{}');
      basicMember.email = manualEmail;
      localStorage.setItem('basicMemberApplication', JSON.stringify(basicMember));
      
      setShowEmailPrompt(false);
      setManualEmail('');
      
      // Retry the upgrade with the provided email
      // We'll need to pass the target level to this function
      // For now, just close the prompt and let user try again
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-lg text-slate-600">Loading your member portal...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Shield className="h-8 w-8 text-green-600 mr-3" />
              <h1 className="text-xl font-semibold text-gray-900">
                Basic Member Portal
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <Badge className="bg-green-100 text-green-800">
                Basic Membership
              </Badge>
              <Button variant="outline" size="sm" onClick={onBack}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Application
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Welcome Banner */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="bg-gradient-to-r from-green-600 to-green-700 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold mb-2">
                  Welcome to Legacy Property Association!
                </h2>
                <p className="text-green-100 text-lg">
                  Your Basic membership provides access to essential documents, forms, and educational resources.
                </p>
              </div>
              <div className="hidden md:block">
                <CheckCircle className="w-16 h-16 text-green-200" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <Tabs defaultValue="documents" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="documents" className="text-xs sm:text-sm">
              <span className="hidden sm:inline">Documents & Forms</span>
              <span className="sm:hidden">Documents</span>
            </TabsTrigger>
            <TabsTrigger value="skills" className="text-xs sm:text-sm">
              <span className="hidden sm:inline">Professional Network</span>
              <span className="sm:hidden">Network</span>
            </TabsTrigger>
            <TabsTrigger value="education" className="text-xs sm:text-sm">
              <span className="hidden sm:inline">Educational Resources</span>
              <span className="sm:hidden">Education</span>
            </TabsTrigger>
            <TabsTrigger value="account" className="text-xs sm:text-sm">
              <span className="hidden sm:inline">Account Info</span>
              <span className="sm:hidden">Account</span>
            </TabsTrigger>
          </TabsList>

          {/* Documents Tab */}
          <TabsContent value="documents" className="space-y-6">
            <div className="grid md:grid-cols-3 gap-6">
              {categories.slice(0, 2).map((category, index) => {
                const Icon = getCategoryIcon(category.name);
                const categoryDocs = getDocumentsByCategory(category.name);
                
                return (
                  <Card key={index} className="hover:shadow-lg transition-all duration-300">
                    <CardHeader>
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                          <Icon className="w-6 h-6 text-green-600" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">{category.name}</CardTitle>
                          <p className="text-sm text-slate-500">{category.document_count} documents</p>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-slate-600 mb-4">{category.description}</p>
                      <div className="space-y-2">
                        {categoryDocs.slice(0, 3).map((doc) => (
                          <div key={doc.id} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                            <div className="flex items-center space-x-2">
                              <FileText className="w-4 h-4 text-slate-400" />
                              <span className="text-sm text-slate-700 truncate">{doc.title}</span>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => handleDocumentDownload(doc.id, doc.title, e)}
                            >
                              <Download className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                      {categoryDocs.length > 3 && (
                        <p className="text-xs text-slate-500 mt-2">
                          +{categoryDocs.length - 3} more documents
                        </p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}

              {/* Upgrade Card */}
              <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
                <CardHeader>
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                      <Star className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg text-amber-800">Upgrade Benefits</CardTitle>
                      <p className="text-sm text-amber-600">Standard & Premium Memberships</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-amber-700 mb-4">
                    Unlock property management tools, premium support, and automated reporting tools.
                  </p>
                  <div className="space-y-2 text-sm text-amber-700 mb-4">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-amber-600" />
                      <span>Property portfolio tracking</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-amber-600" />
                      <span>Tenant payment coordination</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-amber-600" />
                      <span>Maintenance request system</span>
                    </div>
                  </div>
                  
                  <div className="mb-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <h4 className="font-semibold text-purple-800 text-sm mb-2">💎 Premium Exclusive Benefits:</h4>
                    <div className="space-y-1 text-xs text-purple-700">
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="w-3 h-3 text-purple-600" />
                        <span>Reporting Tools with all income/expenses</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="w-3 h-3 text-purple-600" />
                        <span>Accounting Software Integration</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="w-3 h-3 text-purple-600" />
                        <span>Land Trust Education and Support</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="w-3 h-3 text-purple-600" />
                        <span>Eviction Support</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="w-3 h-3 text-purple-600" />
                        <span>Full administrative support</span>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {upgradeError && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-700">{upgradeError}</p>
                      </div>
                    )}
                    
                    {showEmailPrompt && (
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                        <p className="text-sm text-blue-800 font-medium">
                          Please provide your email address to continue with the upgrade:
                        </p>
                        <div className="flex space-x-2">
                          <input
                            type="email"
                            value={manualEmail}
                            onChange={(e) => setManualEmail(e.target.value)}
                            placeholder="your.email@example.com"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                          <button
                            onClick={handleEmailSubmit}
                            disabled={!manualEmail || !manualEmail.includes('@')}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setShowEmailPrompt(false)}
                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-400"
                          >
                            Cancel
                          </button>
                        </div>
                        <p className="text-xs text-blue-600">
                          💡 This should be the same email you used when applying for Basic membership.
                        </p>
                      </div>
                    )}
                    
                    <Button 
                      className="w-full bg-amber-600 hover:bg-amber-700"
                      onClick={() => handleUpgrade('Standard')}
                      disabled={upgrading || showEmailPrompt}
                    >
                      {upgrading ? 'Processing...' : 'Upgrade to Standard - $50/month'}
                    </Button>
                    <Button 
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                      onClick={() => handleUpgrade('Premium')}
                      disabled={upgrading || showEmailPrompt}
                    >
                      {upgrading ? 'Processing...' : 'Upgrade to Premium - $100/month'}
                      <span className="text-xs ml-2">📊 Reporting Tools</span>
                    </Button>
                    
                    <div className="pt-2 border-t border-gray-200">
                      <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={() => setShowSubscriptionManagement(true)}
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        Manage Subscription & Billing
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Professional Network Tab - GC/Labor + RE Agents */}
          <TabsContent value="skills" className="space-y-6">
            <div className="mb-6">
              <Card className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-bold mb-2 flex items-center">
                        Professional Network
                      </h2>
                      <p className="text-blue-100 text-lg">
                        Connect with licensed contractors, skilled tradespeople, and exclusive real estate agents within our PMA.
                      </p>
                    </div>
                    <div className="hidden md:block">
                      <Users className="w-16 h-16 text-blue-200" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sub-tabs for Contractors vs RE Agents */}
            <div className="flex space-x-2 border-b border-gray-200 mb-6">
              <button
                onClick={() => setProfessionalTab('contractors')}
                className={`px-6 py-3 font-medium border-b-2 transition-colors ${
                  professionalTab === 'contractors'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                🔧 GC & Skilled Labor
              </button>
              <button
                onClick={() => setProfessionalTab('re-agents')}
                className={`px-6 py-3 font-medium border-b-2 transition-colors ${
                  professionalTab === 're-agents'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                🏢 Real Estate Agents
              </button>
            </div>

            {/* GC & Skilled Labor Tab */}
            {professionalTab === 'contractors' && (
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Available Specialty Skills in Member Network</CardTitle>
                    <p className="text-slate-600">View our network of PMA members who provide these professional services:</p>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {skillsNetwork.map((item, index) => (
                        <Card key={index} className="p-4 hover:shadow-md transition-shadow">
                          <div className="flex items-center space-x-3">
                            <span className="text-2xl">{item.icon}</span>
                            <div className="flex-1">
                              <h4 className="font-semibold text-slate-800">{item.skill}</h4>
                              <p className="text-sm text-slate-500">
                                {item.members} {item.members === 1 ? 'member' : 'members'} available
                              </p>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>How the Skills Network Works</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-start space-x-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-semibold text-blue-600">1</span>
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-800">Browse Available Skills</h4>
                          <p className="text-sm text-slate-600">View specialty skills offered by fellow PMA members in your area.</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-3">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-semibold text-green-600">2</span>
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-800">Connect Privately</h4>
                          <p className="text-sm text-slate-600">Contact members directly through our secure PMA messaging system.</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-3">
                        <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-semibold text-amber-600">3</span>
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-800">Private Contracts</h4>
                          <p className="text-sm text-slate-600">Work directly under private member agreements, protected by PMA privacy.</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Member Profile & Actions */}
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Network Benefits</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 text-sm">
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span>Private member-to-member referrals</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span>No public advertising required</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span>Protected under PMA privacy</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span>Build trusted professional relationships</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span>Access to member-only projects</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-green-200 bg-green-50">
                  <CardContent className="p-4">
                    <div className="text-center">
                      <h4 className="font-semibold text-green-800 mb-2">Join the Network</h4>
                      <p className="text-sm text-green-700 mb-3">
                        This exclusive feature is available to all Basic members at no additional cost!
                      </p>
                      <Badge className="bg-green-600 text-white">
                        FREE with Basic Membership
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
            )}

            {/* RE Agents Tab */}
            {professionalTab === 're-agents' && (
              <div className="grid lg:grid-cols-3 gap-6">
                {/* RE Agents List */}
                <div className="lg:col-span-2 space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Exclusive Real Estate Agent Network ({reAgents.length} Agents)</CardTitle>
                      <p className="text-slate-600">Connect with our carefully selected real estate professionals:</p>
                    </CardHeader>
                    <CardContent>
                      {reAgents.length > 0 ? (
                        <div className="space-y-4">
                          {reAgents.map((agent) => (
                            <Card key={agent.id} className="p-4 hover:shadow-lg transition-shadow border-blue-200">
                              <div className="flex items-start space-x-4">
                                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                                  <span className="text-2xl">🏢</span>
                                </div>
                                <div className="flex-1">
                                  <h4 className="font-bold text-lg text-slate-800">
                                    {agent.first_name} {agent.last_name}
                                  </h4>
                                  {agent.company_name && (
                                    <p className="text-sm text-slate-600 mb-2">{agent.company_name}</p>
                                  )}
                                  {agent.bio && (
                                    <p className="text-sm text-slate-700 mb-3">{agent.bio}</p>
                                  )}
                                  {agent.specialties && agent.specialties.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mb-3">
                                      {agent.specialties.map((specialty, idx) => (
                                        <Badge key={idx} className="bg-blue-100 text-blue-800">
                                          {specialty}
                                        </Badge>
                                      ))}
                                    </div>
                                  )}
                                  <div className="flex items-center space-x-4 text-sm">
                                    <div className="flex items-center text-yellow-600">
                                      <span className="mr-1">★</span>
                                      <span>{agent.average_rating ? agent.average_rating.toFixed(1) : '0.0'}</span>
                                      <span className="text-gray-500 ml-1">({agent.total_reviews || 0})</span>
                                    </div>
                                    <button 
                                      onClick={() => alert('Contact agent feature coming soon!')}
                                      className="text-blue-600 hover:text-blue-800 font-medium"
                                    >
                                      Contact Agent →
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </Card>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          <p>No real estate agents available yet. Check back soon!</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* RE Network Benefits */}
                <div className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Agent Network Benefits</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3 text-sm">
                        <div className="flex items-center space-x-2">
                          <CheckCircle className="w-4 h-4 text-blue-600" />
                          <span>Exclusive agent network</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <CheckCircle className="w-4 h-4 text-blue-600" />
                          <span>Member-vetted professionals</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <CheckCircle className="w-4 h-4 text-blue-600" />
                          <span>Buying & selling assistance</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <CheckCircle className="w-4 h-4 text-blue-600" />
                          <span>Investment property guidance</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <CheckCircle className="w-4 h-4 text-blue-600" />
                          <span>Refinancing support</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-blue-200 bg-blue-50">
                    <CardContent className="p-4">
                      <div className="text-center">
                        <h4 className="font-semibold text-blue-800 mb-2">Available to All Members</h4>
                        <p className="text-sm text-blue-700 mb-3">
                          Connect with our exclusive agent network as a Basic, Standard, or Premium member!
                        </p>
                        <Badge className="bg-blue-600 text-white">
                          Included with Membership
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Educational Resources Tab */}
          <TabsContent value="education" className="space-y-6">
            <div className="grid lg:grid-cols-2 gap-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <BookOpen className="w-5 h-5 text-blue-600" />
                    <span>Educational Resources</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {getDocumentsByCategory('Educational Resources').map((doc) => (
                      <div key={doc.id} className="border border-slate-200 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-slate-800 mb-1">{doc.title}</h4>
                            <p className="text-sm text-slate-600 mb-2">{doc.description}</p>
                            <div className="flex items-center space-x-4 text-xs text-slate-500">
                              <span>{doc.file_type}</span>
                              <span>Added {new Date(doc.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => handleDocumentDownload(doc.id, doc.title, e)}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-6">
                {/* Member Benefits */}
                <Card className="bg-blue-50">
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Shield className="w-5 h-5 text-blue-600" />
                      <span>Your Member Benefits</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {memberInfo?.available_features?.map((feature, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                          <span className="text-sm text-slate-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Next Steps */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Bell className="w-5 h-5 text-amber-600" />
                      <span>Recommended Next Steps</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {memberInfo?.next_steps?.map((step, index) => (
                        <div key={index} className="flex items-start space-x-2">
                          <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-xs font-semibold text-blue-600">{index + 1}</span>
                          </div>
                          <span className="text-sm text-slate-700">{step}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Account Info Tab */}
          <TabsContent value="account" className="space-y-6">
            <div className="grid lg:grid-cols-2 gap-8">
              <Card>
                <CardHeader>
                  <CardTitle>Membership Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Membership Level:</span>
                      <Badge className="bg-green-100 text-green-800">Basic (FREE)</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Member Since:</span>
                      <span className="font-medium">{new Date().toLocaleDateString()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Application ID:</span>
                      <code className="bg-slate-100 px-2 py-1 rounded text-sm">
                        {applicationData?.applicationId || 'N/A'}
                      </code>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Status:</span>
                      <Badge className="bg-green-100 text-green-800">Active</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Contact & Support</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-slate-800 mb-2">Member Support</h4>
                      <p className="text-slate-600 text-sm mb-2">
                        Need help accessing your documents or have questions about your membership?
                      </p>
                      <p className="text-blue-600 text-sm">
                        Email: info@lpassociation.org<br />
                        Phone: (312) 899-6169
                      </p>
                    </div>
                    
                    <div className="pt-4 border-t border-slate-200">
                      <h4 className="font-semibold text-slate-800 mb-2">Privacy Protection</h4>
                      <p className="text-slate-600 text-sm">
                        As a PMA member, your property affairs and personal information are protected under private contract law.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Contractor modal removed - skills network is now view-only */}

      {/* Subscription Management Modal */}
      {showSubscriptionManagement && (
        <SubscriptionManagement
          memberEmail={applicationData?.email || 
                      JSON.parse(localStorage.getItem('legacyPropertyApplication') || '{}').email ||
                      JSON.parse(localStorage.getItem('basicMemberApplication') || '{}').email}
          onClose={() => setShowSubscriptionManagement(false)}
        />
      )}
    </div>
  );
};

export default BasicMemberPortal;